/*
 *   Copyright 2022  SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { Utils } from "../../utils/utils";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { JsonLib } from "../../utils/jsonLib";
import { GTSLib } from "../../utils/gts.lib";
export class DiscoveryModalComponent {
    constructor() {
        this.data = undefined;
        this.options = new Param();
        this.url = undefined;
        this.debug = false;
        this.tile = undefined;
        this.dashboard = undefined;
        this.showModal = false;
    }
    optionsUpdate(newValue, oldValue) {
        var _a;
        if (!!this.options && typeof this.options === 'string') {
            this.options = JSON.parse(this.options);
        }
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], {
            options: this.options,
            newValue, oldValue
        });
    }
    dataUpdate(newValue, oldValue) {
        var _a;
        if (!!this.data && typeof this.data === 'string') {
            this.data = new JsonLib().parse(this.data);
        }
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['dataUpdate'], { data: this.data, newValue, oldValue });
        this.parseData();
    }
    async open() {
        this.showModal = true;
        return Promise.resolve();
    }
    componentWillLoad() {
        this.LOG = new Logger(DiscoveryModalComponent, this.debug);
        this.parseData();
    }
    parseData() {
        var _a;
        if (GTSLib.isArray(this.data)) {
            this.data = this.data[0];
        }
        if (this.data) {
            if (this.data.macro || this.data.data) {
                this.dashboard = undefined;
                this.tile = this.data;
            }
            else {
                this.tile = undefined;
                this.dashboard = this.data;
            }
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['parseData'], { tile: this.tile, dashboard: this.dashboard });
        }
    }
    closeModal() {
        this.showModal = false;
    }
    render() {
        return h("div", { key: '5b5a007f0f4f13a8608d2360f82e0998a3dd4b41', ref: (el) => this.modal = el }, this.showModal ? h("div", { class: "modal" }, h("div", { class: "modal-content" }, h("span", { class: "close", onClick: () => this.closeModal() }, "\u00D7"), !!this.tile ? h("div", { class: "modal-wrapper" }, !!this.tile.macro
            ? h("discovery-tile", { url: this.tile.endpoint || this.url, type: this.tile.type, "chart-title": this.tile.title, debug: this.debug, options: JSON.stringify(Utils.merge(this.options, this.tile.options)) }, this.tile.macro + ' EVAL')
            : h("discovery-tile-result", { url: this.tile.endpoint || this.url, result: Utils.sanitize(this.tile.data), type: this.tile.type, unit: this.tile.unit, options: Utils.merge(this.options, this.tile.options), debug: this.debug, "chart-title": this.tile.title })) : '', !!this.dashboard ? h("div", { class: "modal-wrapper" }, h("discovery-dashboard", { url: this.url, "dashboard-title": this.dashboard.title, cols: this.dashboard.cols, "cell-height": this.dashboard.cellHeight, debug: this.debug, options: this.options }, `<%
<'
${JSON.stringify(this.dashboard)}
'>
JSON-> %> EVAL`)) : '')) : '');
    }
    static get is() { return "discovery-modal"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-modal.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-modal.css"]
        };
    }
    static get properties() {
        return {
            "data": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "Tile | Dashboard",
                    "resolved": "Dashboard | Tile",
                    "references": {
                        "Tile": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::Tile"
                        },
                        "Dashboard": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::Dashboard"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                }
            },
            "options": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "url": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "url",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "tile": {},
            "dashboard": {},
            "showModal": {}
        };
    }
    static get methods() {
        return {
            "open": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "options",
                "methodName": "optionsUpdate"
            }, {
                "propName": "data",
                "methodName": "dataUpdate"
            }];
    }
}
//# sourceMappingURL=discovery-modal.js.map
